import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, message } from "antd";
import RechargeModal from "../componets/modals/Home/Recharge";
import PayConfirmModal from "../componets/modals/Home/PayConfirm";
import WechatPayModal from "../componets/modals/Home/WechatPayModal";
import { RechargeProduct, useGlobal } from "../contexts/GlobalContext";
import { payment } from "../utils/payment";
import { useTranslation } from "../i18n";

type RechargeType = "member" | "token";

// 收银台(PRD v4 05§4.1 U7):充值/购买的统一入口页。
// 入口:/payment-checkout?type=member|token&projectId=N;复用 RechargeModal 作为收银 UI,
// 首页/各处充值按钮统一跳转到本页,不再各自维护 Modal 链。
const PaymentCheckout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useGlobal();
  const [redeemForm] = Form.useForm();

  const query = new URLSearchParams(location.search);
  const initialType: RechargeType = query.get("type") === "token" ? "token" : "member";
  const projectIdParam = Number(query.get("projectId")) || undefined;

  const [rechargeType] = useState<RechargeType>(initialType);
  const [rechargeProducts, setRechargeProducts] = useState<RechargeProduct[]>([]);
  const [rewardProducts, setRewardProducts] = useState<RechargeProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number>();
  const [tokenAmount, setTokenAmount] = useState(1);
  const [payConfirmVisible, setPayConfirmVisible] = useState(false);
  const [currentPayUrl, setCurrentPayUrl] = useState("");
  const [wechatPayModalVisible, setWechatPayModalVisible] = useState(false);
  const [wechatOrderData, setWechatOrderData] = useState<{
    order_id: string;
    code_url: string;
    amount: number;
  } | null>(null);

  const fetchProducts = useCallback(async () => {
    const code = rechargeType === "token" ? "S" : "P";
    const products = await payment.getProducts(code, token);
    const sorted = [...products].sort((a, b) => (b.price || 0) - (a.price || 0));
    setRechargeProducts(sorted as RechargeProduct[]);
    if (sorted.length > 0) setSelectedProductId(sorted[0].id);
    const rewards = await payment.getRewardProducts(token);
    setRewardProducts(rewards as RechargeProduct[]);
  }, [rechargeType, token]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAlipayPay = async (productId: number, productCount = 1, projectId?: number) => {
    const payUrl = await payment.getAlipayUrl(
      { product_id: productId, product_count: productCount, project_id: projectId ?? projectIdParam },
      token
    );
    if (payUrl) {
      setCurrentPayUrl(payUrl);
      window.open(payUrl, "_blank");
      setPayConfirmVisible(true);
    }
  };

  const handleWechatPay = async (productId: number, productCount = 1, projectId?: number) => {
    const orderData = await payment.createWechatOrder(
      { product_id: productId, product_count: productCount, project_id: projectId ?? projectIdParam },
      token
    );
    if (orderData) {
      setWechatOrderData(orderData);
      setWechatPayModalVisible(true);
    }
  };

  const handleRedeemSubmit = async () => {
    const values = await redeemForm.validateFields();
    const codes = String(values.redeemCodes || "")
      .split("\n")
      .filter((code: string) => code.trim());
    if (codes.length === 0) {
      message.error(t("payment.invalidRedeem"));
      return;
    }
    const result = await payment.redeemCode({ recharge_code: codes, project_id: projectIdParam }, token);
    if (result) {
      message.success(t("payment.redeemSuccess"));
      navigate("/", { replace: true });
      window.location.reload();
    }
  };

  const close = () => {
    // 返回来源页;没有历史时回首页
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", borderRadius: 8, padding: 16 }}>
      <RechargeModal
        visible
        onCancel={close}
        rechargeType={rechargeType}
        rechargeProducts={rechargeProducts}
        rewardProducts={rewardProducts}
        selectedProductId={selectedProductId}
        setSelectedProductId={setSelectedProductId}
        tokenAmount={tokenAmount}
        setTokenAmount={setTokenAmount}
        handleAlipayPay={handleAlipayPay}
        handleWechatPay={handleWechatPay}
        user={user}
        onRedeemSubmit={handleRedeemSubmit}
        redeemForm={redeemForm}
      />
      <PayConfirmModal
        visible={payConfirmVisible}
        onCancel={() => setPayConfirmVisible(false)}
        currentPayUrl={currentPayUrl}
        onConfirm={() => {
          setPayConfirmVisible(false);
          navigate("/", { replace: true });
          window.location.reload();
        }}
      />
      <WechatPayModal
        visible={wechatPayModalVisible}
        onCancel={() => setWechatPayModalVisible(false)}
        orderData={wechatOrderData}
        token={token}
      />
    </div>
  );
};

export default PaymentCheckout;
